// ===================================================================
// SHELTER — Analytics Dashboard
// Summary cards, capacity charts, intake trends, special needs breakdown
// ===================================================================

import { useEffect, useMemo } from 'react';
import {
  Title, Text, Paper, SimpleGrid, Stack, Group, Badge, Tooltip,
} from '@mantine/core';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import {
  SURVIVOR_STATUS_COLORS, SHELTER_STATUS_COLORS,
  SPECIAL_NEEDS_OPTIONS,
} from '../../types/shelter';
import type { SurvivorStatus, ShelterStatus } from '../../types/shelter';
import { primary } from '../../lib/colors';

export default function ShelterAnalytics() {
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    shelters, survivors, families, pets, transfers, referrals,
    loadShelters, loadSurvivors, loadFamilies, loadPets, loadTransfers, loadReferrals,
  } = useShelterStore();

  useEffect(() => {
    loadShelters(); loadSurvivors(); loadFamilies(); loadPets(); loadTransfers(); loadReferrals();
  }, [loadShelters, loadSurvivors, loadFamilies, loadPets, loadTransfers, loadReferrals]);

  // Store already server-side filters by active incident; use directly
  const iShelters = incidentId ? shelters : [];
  const iSurvivors = incidentId ? survivors : [];
  const iFamilies = incidentId ? families : [];
  const iPets = incidentId ? pets : [];
  const iTransfers = incidentId ? transfers : [];
  const iReferrals = incidentId ? referrals : [];

  // Summary stats
  const totalSurvivors = iSurvivors.length;
  const totalShelters = iShelters.length;
  const totalCapacity = iShelters.reduce((s, sh) => s + sh.capacity, 0);
  const totalPopulation = iShelters.reduce((s, sh) => s + sh.currentPopulation, 0);
  const avgUtilization = totalCapacity > 0 ? Math.round((totalPopulation / totalCapacity) * 100) : 0;
  const totalFamilies = iFamilies.length;
  const totalPets = iPets.length;
  const pendingTransfers = iTransfers.filter(t => t.status === 'pending' || t.status === 'in_transit').length;
  const pendingReferrals = iReferrals.filter(r => r.status === 'pending' || r.status === 'in_progress').length;

  // Status distribution
  const statusDist = useMemo(() => {
    const map: Record<string, number> = {};
    iSurvivors.forEach(s => { map[s.status] = (map[s.status] || 0) + 1; });
    return Object.entries(map).sort(([, a], [, b]) => b - a);
  }, [iSurvivors]);

  // Shelter status distribution
  const shelterStatusDist = useMemo(() => {
    const map: Record<string, number> = {};
    iShelters.forEach(s => { map[s.status] = (map[s.status] || 0) + 1; });
    return Object.entries(map);
  }, [iShelters]);

  // Capacity by shelter
  const capacityByShetter = useMemo(() =>
    [...iShelters].sort((a, b) => b.currentPopulation - a.currentPopulation).map(s => ({
      name: s.name,
      population: s.currentPopulation,
      capacity: s.capacity,
      pct: s.capacity > 0 ? Math.round((s.currentPopulation / s.capacity) * 100) : 0,
      status: s.status,
    })),
  [iShelters]);

  // Intake over time (by day)
  const intakeByDay = useMemo(() => {
    const map: Record<string, number> = {};
    iSurvivors.forEach(s => {
      if (s.intakeTimestamp) {
        const day = s.intakeTimestamp.split('T')[0];
        if (day) map[day] = (map[day] || 0) + 1;
      }
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [iSurvivors]);

  const maxIntake = useMemo(() => Math.max(...intakeByDay.map(([, n]) => n), 1), [intakeByDay]);

  // Special needs breakdown
  const specialNeedsDist = useMemo(() => {
    const map: Record<string, number> = {};
    iSurvivors.forEach(s => {
      try {
        const needs: string[] = JSON.parse(s.specialNeeds);
        needs.forEach(n => { map[n] = (map[n] || 0) + 1; });
      } catch { /* skip */ }
    });
    return Object.entries(map).sort(([, a], [, b]) => b - a);
  }, [iSurvivors]);

  const getSpecialNeedLabel = (val: string) =>
    SPECIAL_NEEDS_OPTIONS.find(o => o.value === val)?.label ?? val;

  return (
    <Stack gap="lg">
      <Title order={3} c="black">Shelter Analytics</Title>

      {/* Summary row */}
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <Paper p="sm" radius="md" withBorder ta="center">
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Total Survivors</Text>
          <Text fz={28} fw={800} c={primary[700]}>{totalSurvivors}</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center">
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Total Shelters</Text>
          <Text fz={28} fw={800}>{totalShelters}</Text>
          <Group gap={4} mt={4}>
            {shelterStatusDist.map(([status, count]) => (
              <Badge key={status} size="xs" variant="light" color={SHELTER_STATUS_COLORS[status as ShelterStatus] ?? 'gray'}>
                {status.replace(/_/g, ' ')}: {count}
              </Badge>
            ))}
          </Group>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center">
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Avg Capacity Utilization</Text>
          <Text fz={28} fw={800} c={avgUtilization >= 80 ? '#d97706' : '#16a34a'}>{avgUtilization}%</Text>
          <Text size="xs" c="dimmed">{totalPopulation} / {totalCapacity}</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center">
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Families</Text>
          <Text fz={28} fw={800}>{totalFamilies}</Text>
        </Paper>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <Paper p="sm" radius="md" withBorder ta="center">
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Pets</Text>
          <Text fz={28} fw={800}>{totalPets}</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center">
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Pending Transfers</Text>
          <Text fz={28} fw={800} c={pendingTransfers > 0 ? '#d97706' : undefined}>{pendingTransfers}</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center">
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Pending Referrals</Text>
          <Text fz={28} fw={800} c={pendingReferrals > 0 ? '#d97706' : undefined}>{pendingReferrals}</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center">
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Checked In</Text>
          <Text fz={28} fw={800} c="green">{iSurvivors.filter(s => s.status === 'checked_in').length}</Text>
        </Paper>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* Capacity by shelter */}
        <Paper p="md" radius="md" withBorder>
          <Text fw={700} mb="sm">Capacity by Shelter</Text>
          {capacityByShetter.length === 0 ? (
            <Text size="sm" c="dimmed">No shelters</Text>
          ) : (
            <Stack gap="xs">
              {capacityByShetter.map(s => (
                <Group key={s.name} gap="xs">
                  <Text size="sm" w={140} truncate>{s.name}</Text>
                  <Tooltip label={`${s.population} / ${s.capacity} (${s.pct}%)`}>
                    <div style={{ flex: 1, height: 18, background: 'var(--mantine-color-gray-2)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.min(s.pct, 100)}%`,
                        height: '100%',
                        background: s.pct >= 90 ? '#dc2626' : s.pct >= 70 ? '#d97706' : '#16a34a',
                        borderRadius: 4,
                        transition: 'width 0.3s',
                      }} />
                    </div>
                  </Tooltip>
                  <Text size="xs" w={60} ta="right" fw={500}>{s.pct}%</Text>
                </Group>
              ))}
            </Stack>
          )}
        </Paper>

        {/* Status distribution */}
        <Paper p="md" radius="md" withBorder>
          <Text fw={700} mb="sm">Survivor Status Distribution</Text>
          {statusDist.length === 0 ? (
            <Text size="sm" c="dimmed">No data</Text>
          ) : (
            <>
              <div style={{ display: 'flex', height: 40, gap: 2, borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
                {statusDist.map(([status, count]) => {
                  const pct = totalSurvivors > 0 ? (count / totalSurvivors) * 100 : 0;
                  const color = SURVIVOR_STATUS_COLORS[status as SurvivorStatus] ?? '#6b7280';
                  return (
                    <Tooltip key={status} label={`${status.replace(/_/g, ' ')}: ${count} (${pct.toFixed(1)}%)`}>
                      <div style={{
                        flex: `${pct} 0 0%`,
                        minWidth: pct > 3 ? 30 : 8,
                        height: '100%',
                        background: color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'default',
                      }}>
                        {pct > 10 && <Text size="xs" fw={700} c="white">{count}</Text>}
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
              <Stack gap={4}>
                {statusDist.map(([status, count]) => {
                  const pct = totalSurvivors > 0 ? (count / totalSurvivors) * 100 : 0;
                  const color = SURVIVOR_STATUS_COLORS[status as SurvivorStatus] ?? '#6b7280';
                  return (
                    <Group key={status} justify="space-between">
                      <Group gap="xs">
                        <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: color }} />
                        <Text size="sm" fw={500} tt="capitalize">{status.replace(/_/g, ' ')}</Text>
                      </Group>
                      <Text size="sm" c="dimmed">{count} ({pct.toFixed(1)}%)</Text>
                    </Group>
                  );
                })}
              </Stack>
            </>
          )}
        </Paper>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* Intake over time */}
        <Paper p="md" radius="md" withBorder>
          <Text fw={700} mb="sm">Intake Over Time</Text>
          {intakeByDay.length === 0 ? (
            <Text size="sm" c="dimmed">No intake data</Text>
          ) : (
            <Stack gap="xs">
              {intakeByDay.map(([day, count]) => (
                <Group key={day} gap="xs">
                  <Text size="xs" w={80}>{new Date(day + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</Text>
                  <div style={{ flex: 1, height: 16, background: 'var(--mantine-color-gray-2)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{
                      width: `${(count / maxIntake) * 100}%`,
                      height: '100%',
                      background: primary[700],
                      borderRadius: 4,
                    }} />
                  </div>
                  <Text size="xs" w={40} ta="right">{count}</Text>
                </Group>
              ))}
            </Stack>
          )}
        </Paper>

        {/* Special needs breakdown */}
        <Paper p="md" radius="md" withBorder>
          <Text fw={700} mb="sm">Special Needs Breakdown</Text>
          {specialNeedsDist.length === 0 ? (
            <Text size="sm" c="dimmed">No special needs recorded</Text>
          ) : (
            <Stack gap="xs">
              {specialNeedsDist.map(([need, count]) => {
                const maxNeed = specialNeedsDist[0]?.[1] ?? 1;
                return (
                  <Group key={need} gap="xs">
                    <Text size="xs" w={140} truncate>{getSpecialNeedLabel(need)}</Text>
                    <div style={{ flex: 1, height: 16, background: 'var(--mantine-color-gray-2)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{
                        width: `${(count / maxNeed) * 100}%`,
                        height: '100%',
                        background: '#d97706',
                        borderRadius: 4,
                      }} />
                    </div>
                    <Text size="xs" w={30} ta="right">{count}</Text>
                  </Group>
                );
              })}
            </Stack>
          )}
        </Paper>
      </SimpleGrid>
    </Stack>
  );
}
